import * as React from 'react'
import { StoreProps, castingStoreCanRenderCastingTypeInput, castingStoreFormatCalculatorContent, castingStoreFormatSingleRecurrenceDurationWithoutOptions, castingStoreGetCustomerBoughtCouponsPackInsights, castingStoreGetProgressAndSummaryComplexVariantStep, castingStoreGetProgressAndSummaryForAttachmentsStep, castingStoreGetProgressAndSummaryForCombinationAttributesStep, castingStoreGetProgressAndSummaryForFrequencyStep, castingStoreGetProgressAndSummaryForStartDateStep, castingStoreGetProgressAndSummaryGroupSessionStep, castingStoreGetProgressForStep, castingStoreGetStepTitle, castingStoreGetSummaryForStep, castingStoreHasError, castingStoreIsComplex } from '../../../services/stores/casting.store'
import { formatCouponNameAsString } from '../../plugins/casting/coupon/casting-coupon-picker.component'
import CastingOptionsListTagComponent from '../../plugins/casting/options/casting-options-list-tag.component'
import { I18nService } from '../../../services/utils/i18n.service'
import styled from 'styled-components'
import CastingStepComponent from './casting-step.component'
import CastingPetsComponent from '../../plugins/casting/finalize/casting-pets.component'
import CastingUrssafPaymentComponent from '../../plugins/casting/finalize/casting-urssaf-payment.component'
import { useMountTooltipRef } from './casting-step-use-mount-tooltip-ref.hook'
import FakeInputComponent from '../../shared/fake-input.component'
import CastingParticipantsNumberInputComponent from '../../plugins/casting/participants/casting-participants-number-input.component'
import CastingOfferedServiceCastingTypeComponent from '../../plugins/casting/offered-service/casting-offered-service-casting-type.component'

type Props = StoreProps & { btnOnClick: () => void, mobile?: boolean }

const InputMessage = styled.div`
  color: ${ props => props.theme.colors.clr_error };
  font-size: .875em;
  font-family: lato,sans-serif;
  margin-top: .3571428571em;
`

const i18nScoped = new I18nService(require('./casting-step-2.component.yml'))

export default function CastingStep2Component ({ data, store, mobile, btnOnClick }: Props) {
  const { offeredService, quantity, coupon, selectedOptions, slots, castingType, editedCastingId, enableLodging, selectedQuantityDiscount, market } = data
  const { currentTooltip, mountTooltipRef } = useMountTooltipRef({ data, store, step: 2 })
  const tryFocus = React.useMemo(() => store.tryFocus.bind(store), [store])
  const canFocus = React.useMemo(() => store.canFocus.bind(store), [store])

  const renderedInputQuantity = React.useMemo(() => {
    if (!canFocus('quantity')) return null

    return <div>
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'quantity') }
        onClick={ () => tryFocus('quantity') }
        value={ quantity ? `${quantity} x ${offeredService.name}` : null }
        ref={ mountTooltipRef('quantity') }
        className={ castingStoreHasError(data, 'quantity') ? 'has-errors' : '' } />
    </div>
  }, [canFocus, data, mountTooltipRef, offeredService, quantity, tryFocus])

  const renderedCombinationAttributes = React.useMemo(() => {
    if (!canFocus('combination-attributes')) return null
    const { summaryMessages, missing } = castingStoreGetProgressAndSummaryForCombinationAttributesStep(data, 'combination-attributes')

    return <div className="input--withIcon icon-tags">
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'combination-attributes') }
        value={ summaryMessages }
        onClick={ () => tryFocus('combination-attributes') }
        ref={ mountTooltipRef('combination-attributes') } />

      { missing > 0 && <InputMessage>{ i18nScoped.t('missing_attributes', { count: missing }) }</InputMessage> }
    </div>
  }, [canFocus, data, mountTooltipRef, tryFocus])

  const renderedInputCustomerPlan = React.useMemo(() => {
    if (!canFocus('customer-plan')) return null

    const title = castingStoreGetSummaryForStep(data, store, 'customer-plan')
    const progress = castingStoreGetProgressForStep(data, store, 'customer-plan')

    return <div className={ `input--withIcon icon-arrows-rotate-${market.currency === 'EUR' ? 'euro' : 'dollar'}` }>
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'customer-plan') }
        onClick={ () => tryFocus('customer-plan') }
        value={ title === '-' ? null : title }
        ref={ mountTooltipRef('customer-plan') }
        className={ castingStoreHasError(data, 'customer_plan') ? 'has-errors' : '' } />

      { progress < 100 && <InputMessage>{ i18nScoped.t('undefined_customer_plan_expiration_action') }</InputMessage> }
    </div>
  }, [canFocus, data, market.currency, mountTooltipRef, store, tryFocus])

  const renderedInputCoupon = React.useMemo(() => {
    if (!canFocus('coupon')) return null
    const couponsTitle = coupon ? formatCouponNameAsString(coupon) : null
    const { remainingBoughtCouponsPacksQuantity } = castingStoreGetCustomerBoughtCouponsPackInsights(data)
    const progress = castingStoreGetProgressForStep(data, store, 'coupon')

    return <div className={ `input--withIcon icon-${market.currency === 'EUR' ? 'euro' : 'dollar'}` }>
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'coupon') }
        onClick={ () => tryFocus('coupon') }
        value={ couponsTitle || (remainingBoughtCouponsPacksQuantity > 0 ? i18nScoped.t('use_already_paid_coupons_pack') : null) }
        ref={ mountTooltipRef('coupon') }
        className={ castingStoreHasError(data, 'coupon') ? 'has-errors' : '' } />

      { progress < 100 && <InputMessage>{ i18nScoped.t('undefined_coupons_pack') }</InputMessage> }
    </div>
  }, [canFocus, coupon, data, market.currency, mountTooltipRef, store, tryFocus])

  const renderedInputQuantityDiscount = React.useMemo(() => {
    if (!canFocus('quantity-discount')) return null
    
    return <div className="input--withIcon icon-percentage">
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'quantity-discount') }
        onClick={ () => tryFocus('quantity-discount') }
        value={ selectedQuantityDiscount ? castingStoreGetSummaryForStep(data, store, 'quantity-discount') : '' }
        ref={ mountTooltipRef('quantity-discount') }
        className={ castingStoreHasError(data, 'quantity-discount') ? 'has-errors' : '' } />
      
      { offeredService.require_selected_quantity_discount && !selectedQuantityDiscount && <InputMessage>{ i18nScoped.t('undefined_selected_quantity_discount') }</InputMessage> }
    </div>
  }, [canFocus, data, mountTooltipRef, offeredService.require_selected_quantity_discount, selectedQuantityDiscount, store, tryFocus])

  const renderedGroupSessionInput = React.useMemo(() => {
    if (!canFocus('group-session')) return null

    return <div className="input--withIcon icon-calendar">
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'group-session') }
        onClick={ () => tryFocus('group-session') }
        value={ castingStoreGetProgressAndSummaryGroupSessionStep(data).summaryMessages }
        ref={ mountTooltipRef('group-session') }
        className={ castingStoreHasError(data, 'group-session') ? 'has-errors' : '' } />
    </div>
  }, [canFocus, data, mountTooltipRef, tryFocus])

  const renderedComplexVariantInput = React.useMemo(() => {
    if (!canFocus('complex-variant')) return null

    return <div className="input--withIcon icon-calendar">
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'complex-variant') }
        onClick={ () => tryFocus('complex-variant') }
        value={ castingStoreGetProgressAndSummaryComplexVariantStep(data).summaryMessages }
        ref={ mountTooltipRef('complex-variant') }
        className={ castingStoreHasError(data, 'complex-variant') ? 'has-errors' : '' } />
    </div>
  }, [canFocus, data, mountTooltipRef, tryFocus])

  const renderedInputDurationCalculator = React.useMemo(() => {
    if (!canFocus('duration')) return null

    return <div className="input--withIcon icon-clock">
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'duration') }
        onClick={ () => tryFocus('duration') }
        value={ offeredService.calculator_hide_duration ? castingStoreFormatCalculatorContent(data) : castingStoreFormatSingleRecurrenceDurationWithoutOptions(data) }
        ref={ mountTooltipRef('duration') }
        className={ castingStoreHasError(data, 'duration') ? 'has-errors' : '' } />
    </div>
  }, [canFocus, data, mountTooltipRef, offeredService, tryFocus])

  const renderedInputFrequency = React.useMemo(() => {
    if (castingType !== 'casting_recurring' || castingStoreIsComplex(data) || offeredService.enable_groups) return null
    const { summaryMessages } = castingStoreGetProgressAndSummaryForFrequencyStep(data, store)
    const sumaryWithContent = summaryMessages.filter(_ => _ !== ' - ').length > 0
    const weeksInterval = slots.length > 0 && slots[0].weeks_interval
    const missingTimespan = weeksInterval &&
      !!slots.find(_ => _.allowed_patterns.length === 0) &&
      offeredService.rentable_unit_of_work !== 'day' &&
      !offeredService.no_start_time_nor_duration_asked_to_customer

    return <div className="input--withIcon icon-frequency">
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'frequency') }
        onClick={ () => tryFocus('frequency') }
        value={ sumaryWithContent ? summaryMessages.join(', ') : null }
        ref={ mountTooltipRef('frequency') }
        className={ castingStoreHasError(data, 'weeks_interval') || missingTimespan ? 'has-errors' : '' } />

      { missingTimespan && <InputMessage>{ i18nScoped.t('undefined_timespan') }</InputMessage> }
    </div>
  }, [castingType, data, mountTooltipRef, offeredService, slots, store, tryFocus])

  const renderedInputComplex = React.useMemo(() => {
    if (!castingStoreIsComplex(data)) return null
    const { summaryMessages } = castingStoreGetProgressAndSummaryForFrequencyStep(data, store)
    const sumaryWithContent = summaryMessages.filter(_ => _ !== ' - ').length > 0

    return <div className="input--withIcon icon-calendar">
      <FakeInputComponent
        placeholder={ i18nScoped.t('complex_input') }
        onClick={ () => tryFocus('complex') }
        value={ sumaryWithContent ? summaryMessages.join(', ') : null }
        ref={ mountTooltipRef('complex') }
        className={ castingStoreHasError(data, 'weeks_interval') ? 'has-errors' : '' } />
    </div>
  }, [data, mountTooltipRef, store, tryFocus])

  const renderedInputStartDate = React.useMemo(() => {
    if (!canFocus('start-date')) return null
    const startDate = slots.length > 0 && slots[0].start_date
    const canShowTimeWarnings = castingType === 'casting_one_shot' && !castingStoreIsComplex(data) && startDate && offeredService.rentable_unit_of_work !== 'day' && !enableLodging
    const missingStartHour = canShowTimeWarnings && !(slots[0].allowed_patterns[0] || { from_hour: null }).from_hour
    const missingEndHour = canShowTimeWarnings && offeredService['customer_choose_end_date?'] && !(slots[0].allowed_patterns[0] || { until_hour: null }).until_hour
    const missingEndDate = offeredService.rentable_unit_of_work === 'day' && startDate && !slots[0]?.duration

    return <div className="input--withIcon icon-calendar">
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'start-date') }
        onClick={ () => tryFocus('start-date') }
        value={ startDate ? castingStoreGetProgressAndSummaryForStartDateStep(data).summaryMessages : null }
        ref={ mountTooltipRef('start-date') }
        className={ castingStoreHasError(data, 'start_date') || ![0, 100].includes(castingStoreGetProgressForStep(data, store, 'start-date')) ? 'has-errors' : '' } />

      { missingStartHour && <InputMessage>{ i18nScoped.t('undefined_start_hour') }</InputMessage> }
      { missingEndHour && <InputMessage>{ i18nScoped.t('undefined_end_hour') }</InputMessage> }
      { missingEndDate && <InputMessage>{ i18nScoped.t('undefined_end_date') }</InputMessage> }
    </div>
  }, [canFocus, castingType, data, enableLodging, mountTooltipRef, offeredService, slots, store, tryFocus])

  const renderedEndDateRecurrence = React.useMemo(() => {
    if (!canFocus('end-date')) return null

    return <div className="input--withIcon icon-calendar">
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'end-date') }
        onClick={ () => tryFocus('end-date') }
        value={ castingStoreGetSummaryForStep(data, store, 'end-date') }
        ref={ mountTooltipRef('end-date') }
        className={ castingStoreHasError(data, 'end_date') ? 'has-errors' : ' ' }/>
    </div>
  }, [canFocus, data, mountTooltipRef, store, tryFocus])

  const renderedLodgingIcal = React.useMemo(() => {
    if (!canFocus('lodging-ical')) return null

    return <div>
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'lodging-ical') }
        onClick={ () => tryFocus('lodging-ical') }
        value={ castingStoreGetSummaryForStep(data, store, 'lodging-ical') }
        ref={ mountTooltipRef('lodging-ical') }
        className={ castingStoreHasError(data, 'lodging-ical') ? 'has-errors' : ' ' } />
    </div>
  }, [canFocus, data, mountTooltipRef, store, tryFocus])

  const renderedLodgingCheckInOut = React.useMemo(() => {
    if (!canFocus('lodging-check-in-out')) return null
    const formattedValue = castingStoreGetSummaryForStep(data, store, 'lodging-check-in-out')

    return <div>
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'lodging-check-in-out') }
        onClick={ () => tryFocus('lodging-check-in-out') }
        value={ formattedValue === '-' ? '' : formattedValue }
        ref={ mountTooltipRef('lodging-check-in-out') }
        className={ castingStoreHasError(data, 'lodging-check-in-out') ? 'has-errors' : ' ' } />
    </div>
  }, [canFocus, data, mountTooltipRef, store, tryFocus])

  const renderedBackofficeAppointment = React.useMemo(() => {
    if (!canFocus('backoffice-appointment')) return null

    return <div>
      <FakeInputComponent
        placeholder={ castingStoreGetStepTitle(data, 'backoffice-appointment') }
        onClick={ () => store.tryFocus('backoffice-appointment') }
        value={ castingStoreGetSummaryForStep(data, store, 'backoffice-appointment') }
        ref={ mountTooltipRef('backoffice-appointment') }
        className={ 'js-test-fake-appointment ' + (castingStoreHasError(data, 'backoffice-appointment') ? 'has-errors' : '') } />
    
      { data.appointmentRequiredRanges?.length === 0 && <InputMessage>{ i18nScoped.t('undefined_appointment_required_ranges') }</InputMessage> }
    </div>
  }, [canFocus, data, mountTooltipRef, store])

  const renderedParticipantsNumber = React.useMemo(() => {
    if (!offeredService.require_participants_number) return null

    // Use the legacy i18n because of allowed transation (not yet supported in the new i18n service)
    return <CastingParticipantsNumberInputComponent store={ store } data={ data } />
  }, [data, offeredService.require_participants_number, store])

  const renderedAttachments = React.useMemo(() => {
    if (!canFocus('attachments')) return null
    const { summaryMessages, counts } = castingStoreGetProgressAndSummaryForAttachmentsStep(data)

    return <div className="input--withIcon icon-file">
      <FakeInputComponent
        placeholder={ offeredService.attachments_title }
        onClick={ () => tryFocus('attachments') }
        value={ summaryMessages }
        ref={ mountTooltipRef('attachments') }
        className={ counts.missing > 0 ? 'has-errors' : '' } />
      
      { counts.missing > 0 && !editedCastingId && <InputMessage>{ i18nScoped.t('attachments_missing_n_files', { count: counts.missing }) }</InputMessage> }
    </div>
  }, [canFocus, data, editedCastingId, mountTooltipRef, offeredService, tryFocus])

  const renderedTasksButton = React.useMemo(() => {
    if (!canFocus('tasks')) return null

    return <button
      className="buttonNeutral button--narrower button--block u-mln u-mbs"
      onClick={ () => tryFocus('tasks') }
      children={ castingStoreGetStepTitle(data, 'tasks') }
      ref={ mountTooltipRef('tasks') } />
  }, [canFocus, data, mountTooltipRef, tryFocus])

  const renderedOptionsButton = React.useMemo(() => {
    if (!canFocus('options')) return null

    return <div>
      <button
        className="buttonNeutral button--narrower button--block u-mbs"
        onClick={ () => tryFocus('options') }
        children={ castingStoreGetStepTitle(data, 'options') }
        ref={ mountTooltipRef('options') } />

      { offeredService.require_at_least_one_selected_option && selectedOptions.length === 0 && <InputMessage>{ i18nScoped.t('undefined_selected_option') }</InputMessage> }
      <CastingOptionsListTagComponent className="u-mts u-mbs" store={ store } data={ data }  />
    </div>
  }, [canFocus, data, mountTooltipRef, offeredService, selectedOptions.length, store, tryFocus])

  return <CastingStepComponent
    key="2"
    btnText={ i18nScoped.t('next') }
    btnOnClick={ btnOnClick }
    btnDisabled={ !store.validatesLegacyCastingStep(2) }
    className={ (mobile ? 'mobile' : '') + ' js-casting-step-2' }
    tooltip={ mobile ? undefined : currentTooltip }>
    <CastingOfferedServiceCastingTypeComponent store={ store } data={ data } />

    { (castingType || !castingStoreCanRenderCastingTypeInput(data)) && <>
      { renderedInputQuantity }
      { renderedCombinationAttributes }
      { renderedInputCustomerPlan }
      { renderedInputCoupon }
      { renderedInputQuantityDiscount }
      { renderedGroupSessionInput }
      { renderedComplexVariantInput }
      { renderedInputDurationCalculator }
      { renderedInputFrequency }
      { renderedInputComplex }
      { renderedInputStartDate }
      { renderedEndDateRecurrence }
      { renderedLodgingIcal }
      { renderedLodgingCheckInOut }
      { renderedBackofficeAppointment }
      { renderedParticipantsNumber }
      { renderedAttachments }
      { renderedTasksButton }
      { renderedOptionsButton }

      <button
        onClick={ () => store.tryFocus('message') }
        className="buttonNeutral button--narrower button--block u-mln u-mbs"
        children={ castingStoreGetStepTitle(data, 'message') }
        ref={ mountTooltipRef('message') } />
    </> }

    <div className="u-mtm" />
    <CastingPetsComponent store={ store } data={ store.data } />
    <CastingUrssafPaymentComponent store={ store } data={ store.data } />
  </CastingStepComponent>
}
